import {CommonModule} from '@angular/common';
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {GlobalPaymentSplitRule} from '@gatewee/types/payment';
import {RecipientBankAccount} from '@gatewee/types/recipient-bank-account';
import {RecipientPixKey} from '@gatewee/types/recipient-pix-keys';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable, Subject, takeUntil} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeInputV2Component} from '../../gatewee-input-v2/gatewee-input-v2.component';
import {GateweeSelectV3Component} from '../../gatewee-select-v3/gatewee-select-v3.component';

export interface GlobalPaymentSplitRulesDataService {
  getPixKeys(recipientId: number): Observable<RecipientPixKey[]>;
  getBankAccounts(recipientId: number): Observable<RecipientBankAccount[]>;
}

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
  globalPaymentSplitRulesDataService?: GlobalPaymentSplitRulesDataService;
}

@Component({
  selector: 'gatewee-global-payment-split-rules',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeInputV2Component,
    GateweeSelectV3Component,
  ],
  templateUrl: './global-payment-split-rules.component.html',
})
export class GlobalPaymentSplitRulesComponent implements OnInit, OnDestroy {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
  dataService?: GlobalPaymentSplitRulesDataService;

  private readonly destroy$ = new Subject<void>();

  pixKeyItems: {label: string; value: number}[] = [];
  bankAccountItems: {label: string; value: number}[] = [];
  dataLoaded = false;
  dataUnavailable = false;

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    const data = this.context.data;
    this.id = data.id;
    this.setting = data.setting;
    this.editSettingFn = data.editSettingFn;
    this.dataService = data.globalPaymentSplitRulesDataService;
  }

  ngOnInit(): void {
    this.initializeForm([]);
    if (this.dataService) {
      this.loadData();
    } else {
      this.dataUnavailable = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get rulesArray(): FormArray {
    return this.formGroup?.get('rules') as FormArray;
  }

  addRule(): void {
    this.rulesArray.push(this.createRuleGroup());
  }

  removeRule(index: number): void {
    if (this.rulesArray.length > 1) {
      this.rulesArray.removeAt(index);
    }
  }

  async onSubmit(): Promise<void> {
    if (this.formGroup.invalid || this.submitting || this.dataUnavailable) {
      this.formGroup?.markAllAsTouched();
      return;
    }

    this.submitting = true;
    const rules = this.serializeRules();
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: JSON.stringify(rules),
    };

    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: Error) => {
        console.error('Error updating setting:', error);
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }

  private loadData(): void {
    if (!this.dataService) return;

    this.dataService
      .getPixKeys(this.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (keys: RecipientPixKey[]) => {
          this.pixKeyItems = keys.map((k: RecipientPixKey) => ({
            value: k.id,
            label: k.key ? `${k.key} (${k.type})` : `ID: ${k.id}`,
          }));
          this.dataService!.getBankAccounts(this.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (accounts: RecipientBankAccount[]) => {
                this.bankAccountItems = accounts.map(
                  (a: RecipientBankAccount) => ({
                    value: a.id,
                    label: a.pixKey
                      ? `${a.pixKey} - ${a.holderName ?? a.id}`
                      : `ID: ${a.id}`,
                  }),
                );
                this.dataLoaded = true;
                this.initializeForm(this.parseExistingValue());
              },
              error: () => {
                this.dataUnavailable = true;
                this.initializeForm([]);
              },
            });
        },
        error: () => {
          this.dataUnavailable = true;
          this.initializeForm([]);
        },
      });
  }

  private parseExistingValue(): GlobalPaymentSplitRule[] {
    const value = this.setting?.value;
    if (Array.isArray(value)) {
      return value as GlobalPaymentSplitRule[];
    }
    if (typeof value === 'string' && value.trim() !== '') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  private createRuleGroup(rule?: GlobalPaymentSplitRule): FormGroup {
    return this.fb.group({
      scope: ['pixKey' as const],
      pixKeyId: [rule?.pixKeyId ?? null, Validators.required],
      type: ['rate' as const],
      value: [
        rule?.value ?? null,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      bankAccountId: [rule?.bankAccountId ?? null, Validators.required],
      description: [rule?.description ?? ''],
    });
  }

  private initializeForm(rules: GlobalPaymentSplitRule[]): void {
    const groups =
      rules.length > 0
        ? rules.map((r: GlobalPaymentSplitRule) => this.createRuleGroup(r))
        : [this.createRuleGroup()];
    this.formGroup = this.fb.group({
      rules: this.fb.array(groups),
    });
  }

  private serializeRules(): GlobalPaymentSplitRule[] {
    const raw = this.rulesArray?.value ?? [];
    return raw.map(
      (r: {
        scope: 'pixKey';
        pixKeyId: number;
        type: 'rate';
        value: number;
        bankAccountId: number;
        description?: string;
      }) =>
        ({
          scope: 'pixKey',
          pixKeyId: Number(r.pixKeyId),
          type: 'rate',
          value: Number(r.value),
          bankAccountId: Number(r.bankAccountId),
          ...(r.description?.trim() ? {description: r.description.trim()} : {}),
        } as GlobalPaymentSplitRule),
    );
  }
}
